import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { CashRegister } from '@api/CashRegister/CashRegister';
import { getCashRegisterDate } from '@utils/CashRegisters';

/**
 * Alta y edición de cajas desde el panel del admin.
 *
 * En edición SOLO se tocan el fondo inicial y la fecha/hora de apertura: son los
 * campos que se capturan mal. La sucursal y el responsable NO se editan porque
 * mover una caja de sucursal descuadraría cortes e informes ya revisados.
 */
interface ModalCashRegisterAdminProps {
    open: boolean;
    onClose: () => void;
    /** Sin caja = alta; con caja = edición */
    cashRegister?: CashRegister;
    /** Sucursal preseleccionada al dar de alta */
    branchOfficeId?: string;
    branchOffices: BranchOffice[];
    /** Día que está viendo el admin: la caja nueva nace ahí, no hoy */
    defaultDate: Date;
    onSubmit: (values: { branchOfficeId: string; date: Date; initialCash: number }) => Promise<void>;
}

const toInputValue = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

const ModalCashRegisterAdmin: React.FunctionComponent<ModalCashRegisterAdminProps> = (props) => {
    const { open, onClose, cashRegister, branchOfficeId, branchOffices, defaultDate, onSubmit } = props;
    const isEdit = !!cashRegister;

    const [branch, setBranch] = useState<string>('');
    const [dateValue, setDateValue] = useState<string>('');
    const [initialCash, setInitialCash] = useState<string>('0');
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        if (!open) return;
        setBranch(cashRegister?.branchOfficeId || branchOfficeId || '');
        // Una caja nueva se abre en el DÍA que el admin está viendo (a las 9:00 si
        // es un día pasado, o a la hora actual si es hoy): la fecha manda para los
        // cortes, así que se muestra editable en vez de decidirla en silencio.
        setDateValue(toInputValue(cashRegister ? getCashRegisterDate(cashRegister.date) : defaultDate));
        setInitialCash(String(cashRegister?.initialCash ?? 0));
    }, [open, cashRegister, branchOfficeId, defaultDate]);

    const amount = Number(initialCash);
    const parsedDate = dateValue ? new Date(dateValue) : undefined;
    const isValid =
        !!branch && !!parsedDate && !Number.isNaN(parsedDate.getTime()) && !Number.isNaN(amount) && amount >= 0;

    const handleSubmit = async () => {
        if (!isValid || !parsedDate) return;
        setSaving(true);
        await onSubmit({ branchOfficeId: branch, date: parsedDate, initialCash: amount });
        setSaving(false);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15 } }}>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700}>
                        {isEdit ? 'Editar caja' : 'Abrir caja'}
                    </Typography>
                    <IconButton color="secondary" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} pt={1}>
                    <TextField
                        select
                        fullWidth
                        label="Sucursal"
                        value={branch}
                        disabled={isEdit}
                        onChange={(event) => setBranch(event.target.value)}
                        helperText={isEdit ? 'La sucursal de una caja no se cambia' : undefined}
                    >
                        {branchOffices.map((branchOffice) => (
                            <MenuItem key={branchOffice.id} value={branchOffice.id}>
                                {branchOffice.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        type="datetime-local"
                        label="Fecha y hora de apertura"
                        value={dateValue}
                        onChange={(event) => setDateValue(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText="Mover la fecha cambia en qué día cuenta esta caja"
                    />

                    <TextField
                        fullWidth
                        type="number"
                        label="Fondo inicial"
                        value={initialCash}
                        onChange={(event) => setInitialCash(event.target.value)}
                        InputProps={{ startAdornment: <Typography pr={1}>$</Typography> }}
                    />

                    {isEdit && cashRegister?.username && (
                        <Typography variant="body2" color="text.secondary">
                            {`Abierta por ${cashRegister.username}`}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button fullWidth variant="contained" color="success" disabled={!isValid || saving} onClick={handleSubmit}>
                    {isEdit ? 'Guardar cambios' : 'Abrir caja'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalCashRegisterAdmin;
